import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import moment from 'moment'
import MaterialReactTable from 'material-react-table'
import { MRT_Localization_PT } from 'material-react-table/locales/pt'
import {
  Badge,
  Button,
  Row,
  Col,
  Card,
  CardBody,
  FormGroup,
  Label,
  Spinner,
} from 'reactstrap'
import { Tab, TabList, TabPanel, Tabs } from 'react-tabs'
import 'react-tabs/style/react-tabs.css'
import { downloadFile } from '../../../utils/fc'
import { ButtonLiberarPagamento } from '../../../components/Modals/ModalContrFornInfo/ModalContrFornInfo'
import {
  BUDGET_TERMS,
  buildDetalhamentoRows,
  formatBudgetMoney,
} from './budgetDashboardHelpers'
import {
  fetchFornecedor,
  fetchParcelasByOrcItemIds,
  getEventoVencimento,
} from './budgetItemModalApi'
import './BudgetItemModal.scss'

const mrtCompactProps = {
  muiTableBodyCellProps: { sx: { fontSize: '11px' } },
  muiTableHeadCellProps: {
    sx: {
      '.MuiSvgIcon-root': { fontSize: '18px' },
      fontSize: '12px',
      fontWeight: 'normal',
      padding: '8px',
    },
  },
  muiTablePaginationProps: {
    sx: {
      '.MuiTablePagination-displayedRows': { fontSize: '12px' },
      marginTop: '0px',
      fontSize: '12px',
    },
    labelRowsPerPage: <span style={{ fontSize: '12px' }}>Linhas por página</span>,
    rowsPerPageOptions: [10, 25, 50],
  },
  enableDensityToggle: false,
  enableColumnFilters: false,
  positionPagination: 'bottom',
}

const selectStyle = {
  width: '100%',
  padding: '6px 10px',
  borderRadius: 6,
  border: '1px solid #ced4da',
  fontSize: '0.85rem',
}

const inputStyle = { ...selectStyle }

function defaultMesForAno(ano) {
  const now = moment()
  if (Number(ano) === now.year()) return now.format('YYYY-MM')
  return `${ano}-01`
}

function fmtDate(v) {
  if (!v) return '—'
  return moment(v, 'YYYY-MM-DD').format('DD/MM/YYYY')
}

function parseBrDate(v) {
  if (!v) return null
  const m = moment(v, 'DD/MM/YYYY', true)
  return m.isValid() ? m.format('YYYY-MM-DD') : null
}

function ParcelaLiberarCell({ parcela, onRefresh }) {
  const [fornecedor, setFornecedor] = useState({})
  const [listaParcelas, setListaParcelas] = useState([parcela])

  useEffect(() => {
    setListaParcelas([parcela])
  }, [parcela])

  useEffect(() => {
    if (!parcela.contrForn_parteTab || !parcela.contrForn_parteId) return
    fetchFornecedor(parcela.contrForn_parteTab, parcela.contrForn_parteId).then(setFornecedor)
  }, [parcela])

  const contrato = {
    contrForn_id: parcela.contrForn_id,
    contrFornStatus_id: parcela.contrFornStatus_id,
    contrFornTp_id: parcela.contrFornTp_id,
    contrForn_desc: parcela.contrForn_desc,
    contrForn_parteTab: parcela.contrForn_parteTab,
    contrForn_parteId: parcela.contrForn_parteId,
  }

  const liberado = Number(parcela.contrFornEvento_aprovLiberado) === 1
  const pago = Boolean(parcela.bdro_dtQuit)
  const canLiberar = ![7, 8, 1].includes(Number(parcela.contrFornStatus_id)) && !liberado && !pago

  if (!canLiberar) return '—'

  return (
    <ButtonLiberarPagamento
      fornecedor={fornecedor}
      setFornecedor={setFornecedor}
      contrFornEventoData={parcela}
      getEventos={onRefresh}
      listaParcelas={listaParcelas}
      contrFornData={contrato}
      updateOneEvento={async () => {
        typeof onRefresh === 'function' && onRefresh()
        return parcela
      }}
      setListaParcelas={setListaParcelas}
    />
  )
}

const BudgetDetalhamentoPanel = ({
  ano,
  centros,
  dotAnualRows,
  eventoRows = [],
  selectedCcId,
  selectedOrcItemId,
  onCcIdChange,
  onOrcItemIdChange,
  onOpenItemModal,
}) => {
  const [detTab, setDetTab] = useState(0)
  const [mesRef, setMesRef] = useState(() => defaultMesForAno(ano))
  const [apenasMesSelecionado, setApenasMesSelecionado] = useState(true)
  const [filtroRubrica, setFiltroRubrica] = useState('')
  const [filtroFornecedor, setFiltroFornecedor] = useState('')
  const [periodoIni, setPeriodoIni] = useState('')
  const [periodoFim, setPeriodoFim] = useState('')
  const [parcelasEnriquecidas, setParcelasEnriquecidas] = useState(null)
  const [loadingParcelas, setLoadingParcelas] = useState(false)
  const enrichRequestRef = useRef(0)

  useEffect(() => {
    setMesRef(defaultMesForAno(ano))
    setApenasMesSelecionado(true)
    setParcelasEnriquecidas(null)
  }, [ano])

  const rows = useMemo(
    () => buildDetalhamentoRows(dotAnualRows, selectedCcId, selectedOrcItemId),
    [dotAnualRows, selectedCcId, selectedOrcItemId],
  )

  const rubricasOpcoes = useMemo(() => {
    const set = new Set()
    for (const row of dotAnualRows || []) {
      if (selectedCcId && Number(row.cc_id) !== Number(selectedCcId)) continue
      const nome = row.nivel3 || row.rubrica
      if (nome) set.add(nome)
    }
    return [...set].sort((a, b) => a.localeCompare(b, 'pt-BR'))
  }, [dotAnualRows, selectedCcId])

  const scopedOrcItemIds = useMemo(() => {
    let scoped = dotAnualRows || []
    if (selectedCcId) scoped = scoped.filter(r => Number(r.cc_id) === Number(selectedCcId))
    if (selectedOrcItemId) scoped = scoped.filter(r => Number(r.orcItem_id) === Number(selectedOrcItemId))
    if (filtroRubrica) {
      scoped = scoped.filter(r => (r.nivel3 || r.rubrica) === filtroRubrica)
    }
    return [...new Set(scoped.map(r => Number(r.orcItem_id)).filter(Boolean))]
  }, [dotAnualRows, selectedCcId, selectedOrcItemId, filtroRubrica])

  const scopedOrcItemKey = scopedOrcItemIds.join(',')

  const parcelasBase = useMemo(() => {
    if (!scopedOrcItemIds.length) return []
    const allowed = new Set(scopedOrcItemIds.map(Number))
    return (eventoRows || []).filter(e => (
      Number(e.contrForn_ativo) === 1
      && allowed.has(Number(e.orcItem_id))
    ))
  }, [eventoRows, scopedOrcItemKey])

  const parcelas = parcelasEnriquecidas ?? parcelasBase

  useEffect(() => {
    setParcelasEnriquecidas(null)
  }, [scopedOrcItemKey, selectedCcId, filtroRubrica, filtroFornecedor])

  const loadParcelas = useCallback(async () => {
    if (!scopedOrcItemIds.length) {
      setParcelasEnriquecidas([])
      return
    }
    const requestId = enrichRequestRef.current + 1
    enrichRequestRef.current = requestId
    setLoadingParcelas(true)
    try {
      const raw = await fetchParcelasByOrcItemIds(scopedOrcItemIds)
      if (enrichRequestRef.current !== requestId) return
      setParcelasEnriquecidas(raw.length ? raw : parcelasBase)
    } catch (err) {
      if (enrichRequestRef.current !== requestId) return
      console.error('[budget-dashboard] falha ao enriquecer parcelas:', err)
      setParcelasEnriquecidas(parcelasBase)
    } finally {
      if (enrichRequestRef.current === requestId) {
        setLoadingParcelas(false)
      }
    }
  }, [scopedOrcItemIds, scopedOrcItemKey, parcelasBase])

  const parcelasNoAno = useMemo(() => (parcelas || []).filter(p => {
    const venc = getEventoVencimento(p)
    if (!venc) return false
    return String(venc).slice(0, 4) === String(ano)
  }), [parcelas, ano])

  const parcelasFiltradas = useMemo(() => {
    const mesStart = mesRef ? moment(mesRef, 'YYYY-MM').startOf('month') : null
    const mesEnd = mesRef ? moment(mesRef, 'YYYY-MM').endOf('month') : null
    const pIni = parseBrDate(periodoIni)
    const pFim = parseBrDate(periodoFim)
    const fornQ = filtroFornecedor.trim().toLowerCase()

    return parcelasNoAno.filter(p => {
      const venc = getEventoVencimento(p)
      if (!venc) return false

      if (pIni || pFim) {
        if (pIni && venc < pIni) return false
        if (pFim && venc > pFim) return false
      } else if (apenasMesSelecionado && mesStart && mesEnd) {
        const vencM = moment(venc, 'YYYY-MM-DD')
        if (!vencM.isBetween(mesStart, mesEnd, 'day', '[]')) return false
      }

      if (fornQ && !(p.fornecedor_nome || '').toLowerCase().includes(fornQ)) return false

      return true
    }).sort((a, b) => {
      const da = getEventoVencimento(a) || ''
      const db = getEventoVencimento(b) || ''
      return da.localeCompare(db)
    })
  }, [parcelasNoAno, mesRef, periodoIni, periodoFim, filtroFornecedor, apenasMesSelecionado])

  const contratosProximos90Dias = useMemo(() => {
    const hoje = moment().format('YYYY-MM-DD')
    const limite = moment().add(90, 'days').format('YYYY-MM-DD')
    const fornQ = filtroFornecedor.trim().toLowerCase()
    const allowedItems = scopedOrcItemIds.length
      ? new Set(scopedOrcItemIds.map(Number))
      : null
    const byContrato = new Map()

    for (const ev of eventoRows || []) {
      if (Number(ev.contrForn_ativo) !== 1) continue
      if (allowedItems && !allowedItems.has(Number(ev.orcItem_id))) continue
      if (ev.bdro_dtQuit) continue

      const venc = getEventoVencimento(ev)
      if (!venc || venc < hoje || venc > limite) continue
      if (fornQ && !(ev.fornecedor_nome || '').toLowerCase().includes(fornQ)) continue

      const cid = Number(ev.contrForn_id)
      if (!cid) continue

      if (!byContrato.has(cid)) {
        byContrato.set(cid, {
          contrForn_id: cid,
          contrForn_num: ev.contrForn_num,
          contrForn_desc: ev.contrForn_desc,
          fornecedor_nome: ev.fornecedor_nome,
          orcItem_id: ev.orcItem_id,
          orcItem_desc: ev.orcItem_desc,
          rubrica_nome: ev.rubrica_nome,
          proximoVencimento: venc,
          valorProximo: Number(ev.contrFornEvento_vlr || 0),
          qtdParcelas: 1,
          valorTotal: Number(ev.contrFornEvento_vlr || 0),
        })
      } else {
        const entry = byContrato.get(cid)
        entry.qtdParcelas += 1
        entry.valorTotal += Number(ev.contrFornEvento_vlr || 0)
        if (venc < entry.proximoVencimento) {
          entry.proximoVencimento = venc
          entry.valorProximo = Number(ev.contrFornEvento_vlr || 0)
        }
      }
    }

    return [...byContrato.values()].sort((a, b) => (
      a.proximoVencimento.localeCompare(b.proximoVencimento)
    ))
  }, [eventoRows, scopedOrcItemKey, filtroFornecedor])

  const showCcCol = !selectedCcId

  const totais = useMemo(() => rows.reduce(
    (acc, row) => ({
      vlrIni: acc.vlrIni + row.vlrIni,
      vlrAtu: acc.vlrAtu + row.vlrAtu,
    }),
    { vlrIni: 0, vlrAtu: 0 },
  ), [rows])

  const columns = useMemo(() => {
    const cols = [
      {
        accessorKey: 'itemLabel',
        header: 'Item orçamentário',
        size: 220,
        Cell: ({ row }) => (
          <Button
            color="link"
            className="p-0 text-left"
            style={{ fontSize: 'inherit', whiteSpace: 'normal' }}
            onClick={() => onOpenItemModal?.({
              orcItemId: row.original.orcItemId,
              itemLabel: row.original.itemLabel,
            })}
          >
            {row.original.itemLabel}
          </Button>
        ),
      },
    ]
    if (showCcCol) {
      cols.push({ accessorKey: 'ccNome', header: 'Centro de custo', size: 140 })
    }
    cols.push(
      {
        accessorKey: 'vlrIni',
        header: 'Orçado inicial',
        size: 120,
        Cell: ({ cell }) => formatBudgetMoney(cell.getValue()),
      },
      {
        accessorKey: 'vlrAtu',
        header: BUDGET_TERMS.DISTRIBUIDO,
        size: 120,
        Cell: ({ cell }) => formatBudgetMoney(cell.getValue()),
      },
      {
        accessorKey: 'desvio',
        header: 'Desvio',
        size: 110,
        Cell: ({ cell }) => {
          const v = Number(cell.getValue() || 0)
          if (v === 0) return '—'
          return (
            <span style={v < 0 ? { color: '#f46a6a', fontWeight: 600 } : undefined}>
              {formatBudgetMoney(v)}
            </span>
          )
        },
      },
      {
        id: 'acoes',
        header: '',
        size: 80,
        Cell: ({ row }) => (
          <Button
            size="sm"
            color="light"
            onClick={() => onOpenItemModal?.({
              orcItemId: row.original.orcItemId,
              itemLabel: row.original.itemLabel,
            })}
          >
            Ver
          </Button>
        ),
      },
    )
    return cols
  }, [showCcCol, onOpenItemModal])

  const parcelasColumns = useMemo(() => [
    {
      id: 'vencimento',
      header: 'Vencimento',
      accessorFn: row => getEventoVencimento(row) || '',
      size: 100,
      Cell: ({ row }) => fmtDate(getEventoVencimento(row.original)),
      sortingFn: 'datetime',
    },
    {
      accessorKey: 'orcItem_desc',
      header: 'Item',
      size: 180,
      Cell: ({ row }) => (
        <Button
          color="link"
          className="p-0 text-left"
          style={{ fontSize: 'inherit', whiteSpace: 'normal' }}
          onClick={() => onOpenItemModal?.({
            orcItemId: row.original.orcItem_id,
            itemLabel: row.original.orcItem_desc,
          })}
        >
          {row.original.orcItem_desc || `Item #${row.original.orcItem_id}`}
        </Button>
      ),
    },
    {
      accessorKey: 'contrForn_num',
      header: 'Contrato',
      size: 100,
      accessorFn: row => row.contrForn_num || `#${row.contrForn_id}`,
    },
    { accessorKey: 'fornecedor_nome', header: 'Fornecedor', size: 140 },
    {
      id: 'parcela',
      header: 'Parcela',
      size: 140,
      accessorFn: row => row.contrFornEvento_desc || row.contrFornEvento_num || '—',
    },
    {
      accessorKey: 'contrFornEvento_vlr',
      header: 'Valor',
      size: 100,
      Cell: ({ cell }) => formatBudgetMoney(cell.getValue()),
    },
    {
      id: 'status',
      header: 'Status',
      size: 90,
      enableSorting: false,
      Cell: ({ row }) => {
        const p = row.original
        const liberado = Number(p.contrFornEvento_aprovLiberado) === 1
        const pago = Boolean(p.bdro_dtQuit)
        if (pago) return <Badge color="success" pill>Pago</Badge>
        if (liberado) return <Badge color="info" pill>Liberado</Badge>
        return <Badge color="warning" pill>Pendente</Badge>
      },
    },
    {
      id: 'evidencias',
      header: 'Evidências',
      size: 100,
      enableSorting: false,
      Cell: ({ row }) => {
        const p = row.original
        return (
          <div className="d-flex flex-wrap" style={{ gap: 4 }}>
            {p.arquivo_caminho_evidencia && (
              <Button size="sm" color="link" className="p-0" onClick={() => downloadFile(p.arquivo_caminho_evidencia)}>
                Evid.
              </Button>
            )}
            {p.arquivo_caminho_nota_fiscal && (
              <Button size="sm" color="link" className="p-0" onClick={() => downloadFile(p.arquivo_caminho_nota_fiscal)}>
                NF
              </Button>
            )}
            {!p.arquivo_caminho_evidencia && !p.arquivo_caminho_nota_fiscal && '—'}
          </div>
        )
      },
    },
    {
      id: 'liberar',
      header: 'Liberar',
      size: 100,
      enableSorting: false,
      Cell: ({ row }) => (
        <ParcelaLiberarCell parcela={row.original} onRefresh={loadParcelas} />
      ),
    },
  ], [loadParcelas, onOpenItemModal])

  const contratos90Columns = useMemo(() => [
    {
      accessorKey: 'proximoVencimento',
      header: 'Próx. vencimento',
      size: 110,
      Cell: ({ cell }) => fmtDate(cell.getValue()),
      sortingFn: 'datetime',
    },
    {
      accessorKey: 'contrForn_num',
      header: 'Contrato',
      size: 100,
      accessorFn: row => row.contrForn_num || `#${row.contrForn_id}`,
    },
    { accessorKey: 'fornecedor_nome', header: 'Fornecedor', size: 140 },
    {
      accessorKey: 'orcItem_desc',
      header: 'Item',
      size: 180,
      Cell: ({ row }) => (
        <Button
          color="link"
          className="p-0 text-left"
          style={{ fontSize: 'inherit', whiteSpace: 'normal' }}
          onClick={() => onOpenItemModal?.({
            orcItemId: row.original.orcItem_id,
            itemLabel: row.original.orcItem_desc,
          })}
        >
          {row.original.orcItem_desc || `Item #${row.original.orcItem_id}`}
        </Button>
      ),
    },
    { accessorKey: 'rubrica_nome', header: 'Rubrica', size: 120 },
    { accessorKey: 'qtdParcelas', header: 'Parcelas', size: 80 },
    {
      accessorKey: 'valorProximo',
      header: 'Valor próx.',
      size: 110,
      Cell: ({ cell }) => formatBudgetMoney(cell.getValue()),
    },
    {
      accessorKey: 'valorTotal',
      header: 'Total 90d',
      size: 110,
      Cell: ({ cell }) => formatBudgetMoney(cell.getValue()),
    },
  ], [onOpenItemModal])

  const selectedCc = centros.find(c => String(c.value) === String(selectedCcId))

  const limparFiltros = () => {
    onCcIdChange('')
    typeof onOrcItemIdChange === 'function' && onOrcItemIdChange('')
    setFiltroRubrica('')
    setFiltroFornecedor('')
    setPeriodoIni('')
    setPeriodoFim('')
    setMesRef(defaultMesForAno(ano))
    setApenasMesSelecionado(true)
    setParcelasEnriquecidas(null)
  }

  return (
    <Row>
      <Col lg={3} md={4} className="mb-3 mb-md-0">
        <Card className="h-100 border budget-parcelas-filters">
          <CardBody>
            <h6 className="mb-3">Filtros</h6>

            <FormGroup>
              <Label className="small text-muted">Ano de referência</Label>
              <div className="font-weight-bold">{ano}</div>
            </FormGroup>

            <FormGroup>
              <Label className="small text-muted">Centro de custo</Label>
              <select
                value={selectedCcId || ''}
                onChange={e => onCcIdChange(e.target.value)}
                style={selectStyle}
              >
                <option value="">Todos os centros de custo</option>
                {centros.map(cc => (
                  <option key={cc.value} value={cc.value}>{cc.label}</option>
                ))}
              </select>
            </FormGroup>

            <FormGroup>
              <Label className="small text-muted">Rubrica</Label>
              <select
                value={filtroRubrica}
                onChange={e => setFiltroRubrica(e.target.value)}
                style={selectStyle}
              >
                <option value="">Todas</option>
                {rubricasOpcoes.map(r => (
                  <option key={r} value={r}>{r}</option>
                ))}
              </select>
            </FormGroup>

            <FormGroup>
              <Label className="small text-muted">Fornecedor</Label>
              <input
                type="text"
                value={filtroFornecedor}
                onChange={e => setFiltroFornecedor(e.target.value)}
                placeholder="Nome do fornecedor"
                style={inputStyle}
              />
            </FormGroup>

            <FormGroup>
              <Label className="small text-muted">Mês de vencimento</Label>
              <input
                type="month"
                value={mesRef}
                onChange={e => {
                  setMesRef(e.target.value)
                  setApenasMesSelecionado(true)
                }}
                style={inputStyle}
              />
            </FormGroup>

            <FormGroup>
              <Label className="small text-muted">Período (dd/mm/aaaa)</Label>
              <div className="d-flex" style={{ gap: 6 }}>
                <input
                  type="text"
                  value={periodoIni}
                  onChange={e => setPeriodoIni(e.target.value)}
                  placeholder="De"
                  style={inputStyle}
                />
                <input
                  type="text"
                  value={periodoFim}
                  onChange={e => setPeriodoFim(e.target.value)}
                  placeholder="Até"
                  style={inputStyle}
                />
              </div>
            </FormGroup>

            <Button size="sm" color="light" block onClick={limparFiltros}>
              Limpar filtros
            </Button>
          </CardBody>
        </Card>
      </Col>

      <Col lg={9} md={8}>
        <Card className="border h-100">
          <CardBody>
            <Tabs
              selectedIndex={detTab}
              onSelect={setDetTab}
              className="budget-detalhamento-tabs"
            >
              <TabList>
                <Tab>Dotação anual</Tab>
                <Tab>Parcelas a vencer</Tab>
                <Tab>{`Contratos a vencer (${contratosProximos90Dias.length})`}</Tab>
              </TabList>

              <TabPanel>
                <div className="d-flex flex-wrap justify-content-between align-items-start mb-3 pt-2" style={{ gap: 8 }}>
                  <div>
                    <div className="text-muted small">Resultado do período</div>
                    <div className="font-weight-bold">
                      {rows.length}
                      {' '}
                      item(ns) ·
                      {' '}
                      {formatBudgetMoney(totais.vlrAtu)}
                      {' '}
                      {BUDGET_TERMS.DISTRIBUIDO.toLowerCase()}
                      {selectedCc && (
                        <>
                          {' '}
                          ·
                          {' '}
                          {selectedCc.label}
                        </>
                      )}
                    </div>
                  </div>
                  {selectedCcId && (
                    <Badge color="primary" pill>
                      {selectedOrcItemId ? 'Filtrado por item' : 'Filtrado por CC'}
                    </Badge>
                  )}
                </div>

                {rows.length === 0 && (
                  <p className="text-muted mb-0">
                    Nenhum item com dotação anual encontrado para os filtros selecionados.
                  </p>
                )}

                {rows.length > 0 && (
                  <MaterialReactTable
                    columns={columns}
                    data={rows}
                    getRowId={row => String(row.id)}
                    initialState={{
                      density: 'compact',
                      pagination: { pageIndex: 0, pageSize: 25 },
                      sorting: [{ id: 'vlrAtu', desc: true }],
                    }}
                    enableColumnResizing
                    enableGlobalFilter
                    enablePagination={rows.length > 10}
                    localization={MRT_Localization_PT}
                    {...mrtCompactProps}
                    muiTablePaperProps={{ sx: { boxShadow: 'none', border: '1px solid #eef0f3' } }}
                  />
                )}
              </TabPanel>

              <TabPanel>
                <div className="d-flex flex-wrap justify-content-between align-items-start mb-3 pt-2" style={{ gap: 8 }}>
                  <div>
                    <div className="text-muted small">Parcelas com vencimento</div>
                    <div className="font-weight-bold">
                      {apenasMesSelecionado
                        ? moment(mesRef, 'YYYY-MM').format('MMMM [de] YYYY')
                        : `Todo o ano de ${ano}`}
                      {' '}
                      ·
                      {' '}
                      {parcelasFiltradas.length}
                      {' '}
                      parcela(s)
                      {parcelasNoAno.length !== parcelasFiltradas.length && (
                        <span className="text-muted font-weight-normal small">
                          {' '}
                          (
                          {parcelasNoAno.length}
                          {' '}
                          no ano)
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="d-flex flex-wrap" style={{ gap: 6 }}>
                    {apenasMesSelecionado && parcelasNoAno.length > 0 && (
                      <Button
                        size="sm"
                        color="outline-primary"
                        onClick={() => setApenasMesSelecionado(false)}
                      >
                        Ver todo o ano
                      </Button>
                    )}
                    {!apenasMesSelecionado && (
                      <Button
                        size="sm"
                        color="outline-secondary"
                        onClick={() => setApenasMesSelecionado(true)}
                      >
                        Filtrar por mês
                      </Button>
                    )}
                    <Button size="sm" color="light" onClick={loadParcelas} disabled={loadingParcelas}>
                      Atualizar
                    </Button>
                  </div>
                </div>

                {loadingParcelas && (
                  <div className="text-center py-2">
                    <Spinner size="sm" color="primary" />
                    <span className="text-muted small ml-2">Atualizando evidências e detalhes…</span>
                  </div>
                )}

                {!loadingParcelas && parcelasNoAno.length === 0 && (
                  <p className="text-muted mb-0">
                    Nenhuma parcela com vencimento em
                    {' '}
                    {ano}
                    {' '}
                    para os filtros selecionados.
                    {' '}
                    Use
                    {' '}
                    <strong>Atualizar</strong>
                    {' '}
                    para recarregar evidências de aprovação.
                  </p>
                )}

                {!loadingParcelas && parcelasNoAno.length > 0 && parcelasFiltradas.length === 0 && (
                  <div className="mb-3">
                    <p className="text-muted mb-2">
                      Nenhuma parcela vence em
                      {' '}
                      {moment(mesRef, 'YYYY-MM').format('MMMM [de] YYYY')}
                      , mas há
                      {' '}
                      {parcelasNoAno.length}
                      {' '}
                      parcela(s) em
                      {' '}
                      {ano}
                      .
                    </p>
                    <Button size="sm" color="primary" onClick={() => setApenasMesSelecionado(false)}>
                      Ver todas as parcelas de
                      {' '}
                      {ano}
                    </Button>
                  </div>
                )}

                {parcelasFiltradas.length > 0 && (
                  <MaterialReactTable
                    columns={parcelasColumns}
                    data={parcelasFiltradas}
                    getRowId={row => String(row.contrFornEvento_id)}
                    localization={MRT_Localization_PT}
                    initialState={{
                      density: 'compact',
                      pagination: { pageIndex: 0, pageSize: 25 },
                      sorting: [{ id: 'vencimento', desc: false }],
                    }}
                    enableColumnResizing
                    enableGlobalFilter={false}
                    enablePagination={parcelasFiltradas.length > 10}
                    {...mrtCompactProps}
                    muiTablePaperProps={{ sx: { boxShadow: 'none', border: '1px solid #eef0f3' } }}
                  />
                )}
              </TabPanel>

              <TabPanel>
                <div className="d-flex flex-wrap justify-content-between align-items-start mb-3 pt-2" style={{ gap: 8 }}>
                  <div>
                    <div className="text-muted small">Vencimentos nos próximos 90 dias</div>
                    <div className="font-weight-bold">
                      {contratosProximos90Dias.length}
                      {' '}
                      contrato(s) com parcela(s) a vencer
                    </div>
                  </div>
                </div>

                {contratosProximos90Dias.length === 0 && (
                  <p className="text-muted mb-0">
                    Nenhum contrato com vencimento nos próximos 90 dias para os filtros selecionados.
                  </p>
                )}

                {contratosProximos90Dias.length > 0 && (
                  <MaterialReactTable
                    columns={contratos90Columns}
                    data={contratosProximos90Dias}
                    getRowId={row => String(row.contrForn_id)}
                    localization={MRT_Localization_PT}
                    initialState={{
                      density: 'compact',
                      pagination: { pageIndex: 0, pageSize: 25 },
                      sorting: [{ id: 'proximoVencimento', desc: false }],
                    }}
                    enableColumnResizing
                    enableGlobalFilter={false}
                    enablePagination={contratosProximos90Dias.length > 10}
                    {...mrtCompactProps}
                    muiTablePaperProps={{ sx: { boxShadow: 'none', border: '1px solid #eef0f3' } }}
                  />
                )}
              </TabPanel>
            </Tabs>
          </CardBody>
        </Card>
      </Col>
    </Row>
  )
}

export default BudgetDetalhamentoPanel
